import AWS from "aws-sdk";
import { getAmplifyIdToken } from "./amplifyHelpers";
import store from "src/page/store";
import { AppConfig, getConfig } from "src/config/Config";
import { immediateToast } from "izitoast-react";
import { EMPTY_STRING } from "src/components/constant/constant";

export interface uploadFileToS3Prop {
  file: File;
  fileName: string;
  S3Bucket: string;
  MetaData?: {
    object_id?: string;
    object_type?: string;
    asset_type?: string;
    file_type?: string;
    date?: string;
  };
  contentType?: string;
  suppressToast?: boolean;
}

const setupAWSCredentials = (token: string, config: AppConfig) => {
  AWS.config.credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: config.IDENTITY_POOL_ID,
    Logins: {
      [`cognito-idp.${config.REGION}.amazonaws.com/${config.COGNITO_USER_POOL_ID}`]: token
    }
  });
};

const handleS3Upload = (
  s3: AWS.S3,
  params: AWS.S3.PutObjectRequest,
  fileName: string,
  resolve: (_value: AWS.S3.ManagedUpload.SendData) => void,
  reject: (_reason?: Error) => void,
  suppressToast?: boolean
) => {
  s3.putObject(params)
    .on("httpUploadProgress", (evt) => {
      if (process.env.REACT_APP_BUILD_ENV !== "production") {
        console.log(`Upload Progress: ${Math.round((evt.loaded / evt.total) * 100)}%`);
      }
    })
    .send((err, data) => {
      if (err) {
        reject(err);
        immediateToast("error", {
          message: `Failed to upload ${fileName}`,
          timeout: 3000,
          position: "topCenter"
        });
      } else {
        resolve(data as AWS.S3.ManagedUpload.SendData);
        if (!suppressToast) {
          immediateToast("success", {
            message: `${fileName} uploaded successfully`,
            timeout: 3000,
            position: "topCenter"
          });
        }
      }
    });
};

export const uploadFileToS3 = async (uploadData: uploadFileToS3Prop): Promise<AWS.S3.ManagedUpload.SendData> => {
  const params = {
    ContentDisposition: "inline",
    Body: uploadData.file,
    Bucket: uploadData.S3Bucket,
    Key: uploadData.fileName,
    Metadata: uploadData.MetaData,
    ...(uploadData?.contentType && { ContentType: uploadData.contentType })
  };

  const config = getConfig();
  const { auth } = store.getState();

  AWS.config.update({
    region: config.REGION,
    credentials: new AWS.CognitoIdentityCredentials({
      IdentityPoolId: config.IDENTITY_POOL_ID,
      Logins: {}
    })
  });

  const amplifyToken = await getAmplifyIdToken();

  return new Promise((resolve, reject) => {
    if (amplifyToken) {
      try {
        setupAWSCredentials(amplifyToken, config);

        const s3 = new AWS.S3({ params: { Bucket: uploadData.S3Bucket }, region: config.REGION });

        handleS3Upload(s3, params, uploadData.file.name, resolve, reject, uploadData.suppressToast);
      } catch (e) {
        reject(new Error("Failed to set up AWS credentials for file upload"));
        immediateToast("error", {
          message: `Failed to upload ${uploadData.file.name}`,
          timeout: 3000,
          position: "topCenter"
        });
      }
    } else if (auth.cognitoIdToken) {
      try {
        setupAWSCredentials(auth.cognitoIdToken, config);

        const s3 = new AWS.S3({ params: { Bucket: uploadData.S3Bucket }, region: config.REGION });

        handleS3Upload(s3, params, uploadData.file.name, resolve, reject, uploadData.suppressToast);
      } catch (e) {
        reject(new Error("Failed to set up AWS credentials for file upload"));
        immediateToast("error", {
          message: `Failed to upload ${uploadData.file.name}`,
          timeout: 3000,
          position: "topCenter"
        });
      }
    } else {
      reject(new Error("No valid authentication token found for file upload"));
    }
  });
};

const getS3SignedUrl = async (s3: AWS.S3, params: AWS.S3.GetObjectRequest): Promise<string> => {
  try {
    return await s3.getSignedUrlPromise("getObject", params);
  } catch (e) {
    console.error("Error getting signed URL:", e);
    return EMPTY_STRING;
  }
};

export const getSignedUrlFromS3 = async (
  bucketName: string,
  key: string,
  download: boolean = false
): Promise<string> => {
  if (!key) {
    throw new Error("Key parameter is required for S3 operations");
  }

  const params = {
    Bucket: bucketName,
    Key: key,
    ...(download && { ResponseContentDisposition: "attachment" })
  };

  const config = getConfig();
  const { auth } = store.getState();

  AWS.config.update({
    region: config.REGION,
    credentials: new AWS.CognitoIdentityCredentials({
      IdentityPoolId: config.IDENTITY_POOL_ID,
      Logins: {}
    })
  });

  const amplifyToken = await getAmplifyIdToken();

  return new Promise<string>((resolve) => {
    if (amplifyToken) {
      (async () => {
        try {
          setupAWSCredentials(amplifyToken, config);

          const s3 = new AWS.S3({ params: { Bucket: bucketName }, region: config.REGION });
          const signedUrl = await getS3SignedUrl(s3, params);

          resolve(signedUrl);
        } catch (e) {
          console.error("Error setting up AWS credentials:", e);
          resolve(EMPTY_STRING);
        }
      })();
    } else if (auth.cognitoIdToken) {
      (async () => {
        try {
          setupAWSCredentials(auth.cognitoIdToken as string, config);

          const s3 = new AWS.S3({ params: { Bucket: bucketName }, region: config.REGION });
          const signedUrl = await getS3SignedUrl(s3, params);

          resolve(signedUrl);
        } catch (e) {
          console.error("Error setting up AWS credentials for SSO user:", e);
          resolve(EMPTY_STRING);
        }
      })();
    } else {
      resolve(EMPTY_STRING);
    }
  });
};
